import { useEffect, useState } from "react";
import axios from "axios";
import { Loader2, BookOpen } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  money, fmtDate, ENTRY_LABEL, statusLabel, STATUS_STYLE, PAYOUT_STATUS_STYLE, CHANNEL_LABEL, CHANNEL_STYLE,
} from "./ledgerDisplay";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Which endpoint to call depends on WHO is looking and WHICH kind of payout it is
// (the admin page shows both reseller and distributor payouts). Ownership is enforced
// server-side, so a reseller/distributor can only ever open their own payouts.
const ledgerUrl = (viewer, kind, payoutId) => {
  const id = encodeURIComponent(payoutId);
  if (viewer === "distributor") return `${API}/distributor/payouts/${id}/ledger`;
  if (viewer === "reseller") return `${API}/payouts/${id}/ledger`;
  return `${API}/admin/${kind === "distributor" ? "distributor-payouts" : "payouts"}/${id}/ledger`;
};

// One muted line, and only when the reader would otherwise wonder where the orders are —
// a payout that settled specific orders lists them as rows and needs no explanation.
function contextNote(totals) {
  if (totals.reversed) return "Reversed — the earnings went back to the available balance.";
  if (totals.credits === 0) return "No specific orders are linked to this payout.";
  if (totals.difference > 0) return `${money(totals.difference)} of this payout isn't tied to a specific order.`;
  if (totals.difference < 0) return `The orders it settled add up to ${money(-totals.difference)} more than the payout amount.`;
  return null;
}

export default function PayoutLedgerDialog({ open, onClose, payoutId, payoutKind = "reseller", viewer = "admin" }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open || !payoutId) return;
    let cancelled = false;
    setLoading(true);
    setError("");
    setData(null);
    axios
      .get(ledgerUrl(viewer, payoutKind, payoutId))
      .then(({ data: res }) => { if (!cancelled) setData(res); })
      .catch((err) => {
        if (!cancelled) setError(err.response?.data?.message || "Couldn't load the ledger for this payout.");
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, payoutId, payoutKind, viewer]);

  const payout = data?.payout;
  const note = data ? contextNote(data.totals) : null;
  // The single debit row of a typical payout has no channel; only add the column when a
  // settled order brings one.
  const showChannel = !!data?.entries.some((e) => e.channel);

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose?.(); }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" aria-describedby={undefined} data-testid="payout-ledger-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <BookOpen className="w-5 h-5 text-amber-600" />
            Payout ledger
          </DialogTitle>
          {payout && (
            <div className="flex flex-wrap items-center gap-2 text-sm text-slate-500">
              <span>{payout.owner_name ? `${payout.owner_name} · ` : ""}<span className="font-mono text-xs">{payout.payout_id}</span></span>
              <Badge className={PAYOUT_STATUS_STYLE[payout.status] || "bg-slate-100 text-slate-700"}>{payout.status}</Badge>
            </div>
          )}
        </DialogHeader>

        {loading && (
          <div className="flex justify-center py-10">
            <Loader2 className="w-7 h-7 animate-spin text-amber-600" />
          </div>
        )}

        {!loading && error && (
          <p className="text-center text-red-600 py-8" data-testid="payout-ledger-error">{error}</p>
        )}

        {!loading && data && (
          <div className="space-y-3">
            <div className="border rounded-xl overflow-hidden">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead>Date</TableHead>
                      <TableHead>Entry</TableHead>
                      <TableHead>Order #</TableHead>
                      {showChannel && <TableHead>Channel</TableHead>}
                      <TableHead className="text-right">Credit</TableHead>
                      <TableHead className="text-right">Debit</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.entries.map((e, i) => {
                      const isDebit = e.direction === "debit";
                      const struck = isDebit && e.status === "reversed";
                      return (
                        <TableRow key={e.transaction_id || `debit-${i}`} className={struck ? "text-slate-400" : ""}>
                          <TableCell className="whitespace-nowrap">{fmtDate(e.created_at)}</TableCell>
                          <TableCell className="whitespace-nowrap">{ENTRY_LABEL[e.type] || e.type}</TableCell>
                          <TableCell className="font-mono text-xs">{e.order_number || "—"}</TableCell>
                          {showChannel && (
                            <TableCell>
                              {e.channel ? (
                                <Badge className={CHANNEL_STYLE[e.channel] || "bg-slate-100 text-slate-700"}>
                                  {CHANNEL_LABEL[e.channel] || e.channel}
                                </Badge>
                              ) : "—"}
                            </TableCell>
                          )}
                          <TableCell className="text-right font-medium text-green-600">
                            {!isDebit ? `+${money(e.amount)}` : ""}
                          </TableCell>
                          <TableCell className={`text-right font-medium ${struck ? "line-through text-slate-400" : "text-red-600"}`}>
                            {isDebit ? `−${money(e.amount)}` : ""}
                          </TableCell>
                          <TableCell>
                            <Badge className={STATUS_STYLE[e.status] || "bg-slate-100 text-slate-700"}>
                              {statusLabel(e)}
                            </Badge>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            </div>
            {note && <p className="text-xs text-slate-500" data-testid="payout-ledger-note">{note}</p>}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
